import React from 'react';
import { Box, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import StarIcon from '@mui/icons-material/Star';
import { useGlobalization } from '@Translations/useGlobalization';
import { TFunc } from '@Translations/types';
import { legends } from '../utils.RiskMatrix';
import { getGradeLabel } from '@Utils/gradeLabels';
import {
	CombineMethod,
	GradeName,
	GradeThresholds,
} from './riskSetupDraft.types';
import {
	computeWorkedExample,
	exampleSample,
	formatSampleValue,
	formatScore,
} from './workedExampleMaths';

export const gradeLabel = (grade: GradeName, t: TFunc): string =>
	getGradeLabel(grade, t);

export const gradeColor = (grade: GradeName): string =>
	legends.find(legend => legend.name === grade)?.color ?? '#5b6472';

export interface WorkedExampleProps {
	lofWeighting: number;
	cofWeighting: number;
	riskMethod: CombineMethod | undefined;
	thresholds: GradeThresholds;
	useNormalize: boolean;
	/** Axis maxima the sample pipe scales from (fallback 10 when unusable). */
	maxLoFValue?: number;
	maxCoFValue?: number;
}

const Arrow = () => (
	<Box component="span" sx={{ color: '#c2c9d2' }}>
		→
	</Box>
);

const GradeChip = ({ grade, label }: { grade: GradeName; label: string }) => (
	<>
		<Arrow />
		<Box
			component="span"
			data-cy="worked-example-grade"
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: 0.5,
				fontWeight: 700,
				color: gradeColor(grade),
			}}>
			<StarIcon sx={{ fontSize: 16 }} />
			{label}
		</Box>
	</>
);

export const WorkedExample = (props: WorkedExampleProps) => {
	const { t } = useGlobalization();
	const example = computeWorkedExample(props);
	const sample = exampleSample(props);
	const normalizeNote = t(
		// eslint-disable-next-line quotes -- apostrophe; prettier requires double quotes here
		"With normalization on, the grade depends on the highest-scoring pipe in your network, so it's shown after the model runs.",
	);

	return (
		<Box sx={{ mt: 2 }}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					flexWrap: 'wrap',
					gap: 0.75,
					fontSize: 13.5,
					color: '#7c879a',
				}}>
				<InfoOutlined sx={{ fontSize: 16, color: '#b3bac4' }} />
				<Box
					component="span"
					sx={{ fontWeight: 700, color: '#1b2733' }}>
					{t('Example')}
				</Box>
				<Box component="span">
					{t('A pipe with LoF {{lof}} and CoF {{cof}}', {
						lof: formatSampleValue(sample.lof),
						cof: formatSampleValue(sample.cof),
					})}
				</Box>
				<Box component="span">
					{`${formatScore(example.weightedLof)} ${
						example.operator
					} ${formatScore(example.weightedCof)}`}
				</Box>
				<Arrow />
				<Box
					component="span"
					data-cy="worked-example-score"
					sx={{ fontWeight: 700, color: '#1b2733' }}>
					{formatScore(example.score)}
				</Box>
				{example.grade && (
					<GradeChip
						grade={example.grade}
						label={gradeLabel(example.grade, t)}
					/>
				)}
			</Box>
			{props.useNormalize && (
				<Typography sx={{ fontSize: 12, color: '#5b6472', mt: 0.5 }}>
					{normalizeNote}
				</Typography>
			)}
		</Box>
	);
};
